import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Users } from "lucide-react";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";
import Crest from "./Crest";
import Lightbox from "./Lightbox";

function initials(name) {
  const parts = name.split(" ").filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts[parts.length - 1]?.[0] || "";
  return (first + last).toUpperCase();
}

function PersonCard({ person, index, onOpen, highlighted }) {
  return (
    <article
      id={`kin-${person.id}`}
      data-testid={`notable-person-${person.id}`}
      className={`reveal group flex flex-col sm:flex-row gap-6 border bg-ink2 p-7 sm:p-8 transition-all duration-500 scroll-mt-28 ${
        highlighted
          ? "border-gold shadow-[0_0_0_3px_rgba(196,158,90,0.35)]"
          : "border-[var(--c-border)] hover:border-gold/50"
      }`}
      style={{ animationDelay: `${(index % 2) * 90}ms` }}
    >
      {/* portrait plate — photo if available, else monogram */}
      <div className="shrink-0">
        {person.photo ? (
          <button
            type="button"
            data-testid={`notable-portrait-${person.id}`}
            onClick={() => onOpen({ src: person.photo, caption: person.name })}
            className="block w-24 h-28 overflow-hidden border border-gold/40 bg-ink cursor-pointer hover:border-gold transition-colors"
          >
            <img src={person.photo} alt={person.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
          </button>
        ) : (
          <div className="w-24 h-28 grid place-items-center border border-gold/40 bg-ink relative overflow-hidden">
            <span className="font-display text-4xl text-gold/85 leading-none">{initials(person.name)}</span>
            <span className="absolute inset-x-0 bottom-0 h-px bg-gold/30" />
          </div>
        )}
        <span className="mt-2 block text-center font-label text-[0.5rem] uppercase tracking-[0.14em] text-parchment/35">
          {person.era}
        </span>
      </div>

      <div className="flex-1">
        <span className="font-label text-[0.58rem] uppercase tracking-[0.2em] text-gold">{person.epithet}</span>
        <h3 className="font-display text-2xl sm:text-3xl font-medium text-parchment leading-tight mt-1.5">
          {person.name}
        </h3>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
          <span className="font-label text-[0.58rem] uppercase tracking-[0.14em] text-parchment/50">{person.field}</span>
          <span className="font-label text-[0.58rem] uppercase tracking-[0.14em] text-parchment/50">{person.place}</span>
        </div>
        <p className="mt-4 text-[1.02rem] leading-relaxed text-parchment/65 font-light">{person.bio}</p>
        {person.link && (
          <a
            href={person.link.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`notable-link-${person.id}`}
            className="mt-3 inline-flex w-fit items-center gap-1.5 text-sm text-gold hover:text-parchment transition-colors font-light"
          >
            {person.link.label}
          </a>
        )}
        {person.photos && person.photos.length > 1 && (
          <div className="mt-5 flex gap-2.5" data-testid={`notable-photostrip-${person.id}`}>
            {person.photos.map((src, i) => (
              <button
                type="button"
                key={i}
                onClick={() => onOpen({ src, caption: person.name })}
                className="w-14 h-16 overflow-hidden border border-[var(--c-border)] cursor-pointer hover:border-gold/60 transition-colors"
              >
                <img src={src} alt={`${person.name} ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

export default function NotablePage() {
  useReveal();
  const { c } = useSite();
  const { notable } = c;
  const [active, setActive] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const id = location.hash?.replace("#kin-", "");
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`kin-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(id);
        setTimeout(() => setHighlightId(null), 2600);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [location.hash, notable]);

  return (
    <div className="bg-ink">
      <section className="pt-36 sm:pt-44 pb-16 border-b border-[var(--c-border)]">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-6 text-gold">
            <Users className="w-4 h-4" />
            <span className="eyebrow" data-testid="notable-eyebrow">{notable.eyebrow}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-light leading-[0.98] text-parchment max-w-3xl">
            {notable.title}
          </h1>
          <p className="mt-7 text-lg sm:text-xl leading-relaxed text-parchment/65 font-light max-w-2xl">
            {notable.intro}
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-5">
            {[...notable.people].sort((a, b) => b.born - a.born).map((p, i) => (
              <PersonCard key={p.id} person={p} index={i} onOpen={setActive} highlighted={highlightId === p.id} />
            ))}
          </div>

          <div className="mt-14 flex items-start gap-4 border-t border-[var(--c-border)] pt-8">
            <Crest size={36} className="shrink-0 opacity-70" />
            <p className="text-sm text-parchment/45 font-light leading-relaxed max-w-3xl italic">
              {notable.note}
            </p>
          </div>
        </div>
      </section>

      <Lightbox src={active?.src} caption={active?.caption} onClose={() => setActive(null)} />
    </div>
  );
}
